import React, {useState, useEffect} from "react";
import {TextField, RadioGroup} from "formik-material-ui";
import {
    Typography,
    Grid,
    FormControlLabel,
    TextField as RTextField,
    Button,
    makeStyles,
    Radio,
    Container, Box, CircularProgress
} from "@material-ui/core";
import {Formik, Form, Field, ErrorMessage} from "formik";
import {colorError} from "../../../Constants/theme";
import * as Yup from "yup";
import {Autocomplete} from "@material-ui/lab";
import {insurances, insuranceList} from "../../../Constants/insurance";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";
import {editAnimal} from "../../../redux/pets/pets.actions";
import {petSex} from "../../../interfaces";
import {useSnackbar} from "notistack";
import API from "../../../helpers/apiHelpers";

interface Props {
    changeCount: (number: number) => void;
    isWhiteLabel: boolean;
    pet: any;
    setPet: React.Dispatch<React.SetStateAction<{
        id: number;
        ownerId: number;
        type: string;
        name: string;
        breed: string;
        weight: number | undefined;
        sex: string;
        neuteredSpayed: boolean;
        vaccines: any[];
        medications: any[];
        allergies: any[];
        chronicConditions: any[];
        dateOfBirth: string;
        imageUrl: string;
    }>>;
    petId: number;
    setPetId: React.Dispatch<React.SetStateAction<number>>;
    isOnBoarding: boolean;
}

interface Props {
    changeCount: (number: number) => void;
    isWhiteLabel: boolean;
    pet: any;
    setPet: React.Dispatch<React.SetStateAction<{
        id: number;
        ownerId: number;
        type: string;
        name: string;
        breed: string;
        weight: number;
        sex: string;
        neuteredSpayed: boolean;
        vaccines: any[];
        medications: any[];
        allergies: any[];
        chronicConditions: any[];
        dateOfBirth: string;
        imageUrl: string;
    }>>;
    petId: number;
    setPetId: React.Dispatch<React.SetStateAction<number>>;
}

const useStyle = makeStyles(() => ({
    body: {
        paddingTop: 20,
    },
    cancelButton: {
        border: `1.5px solid ${colorError}`,
        color: colorError
    },
    spacing: {
        paddingTop: 20,
    },
    formError: {
        color: colorError,
        fontSize: "12px"
    }
}));

const PetDetails = ({changeCount, petId, setPetId, pet, setPet, isWhiteLabel, isOnBoarding}: Props) => {
    const classes = useStyle();
    const {enqueueSnackbar} = useSnackbar();
    const dispatch = useAppDispatch();
    const user = useSelector((state: RootState) => state?.user);
    const clinicName = useSelector((state: RootState) => state?.branding.name);
    const [clinic, setClinic] = useState(clinicName);
    const [isLoading, setIsLoading] = useState(false);
    const branding = useSelector((state: RootState) => state?.branding);
    const pets = useSelector((state: RootState) => state?.pets.petsList);
    const partnerType = useSelector((state: RootState) => state?.branding.type);
    const validationSchema = Yup.object().shape({
        sex: Yup.string().required("Required"),
        neuteredSpayed: Yup.boolean().required("Required"),
        clinicEmail: Yup.string().email("Invalid email")
    });
    const [formikValues, setFormikValues] = useState({
        sex: "",
        neuteredSpayed: "",
        insuranceProvider: "",
        otherInsuranceProvider: "",
        clinicEmail: ""
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!pet) {
            (async () => {
                let newPet = await API.getAnimalById(petId);
                setPet(newPet);
            })();
        }
        if (petId && pet) {
            if (petId === 0 || isOnBoarding) {
                return;
            }
            setFormikValues({
                sex: petId !== 0 ? pet.sex as petSex : "",
                neuteredSpayed: pet.neuteredSpayed === true ? "true" : pet.neuteredSpayed === false ? "false" : "",
                insuranceProvider: pet.hasOwnProperty("insuranceProvider") ? insurances.filter(item => item.value === pet["insuranceProvider"])[0].label : "",
                otherInsuranceProvider: pet.hasOwnProperty("otherInsuranceProvider") ? pet["otherInsuranceProvider"] : "",
                clinicEmail: pet?.clinicEmail,
            });
        }
    }, [petId]);

    return (
        <Container className={classes.body} disableGutters={true}>
            <Typography variant="h2">
                Your Pet
            </Typography>
            { isLoading ?
                <Box width="100%" height="300px" display="flex" justifyContent="center" alignItems="center">
                    <CircularProgress/>
                </Box>
                :
                <Formik
                    initialValues={formikValues}
                    enableReinitialize
                    onSubmit={async (values) => {
                        let neuteredSpayed;
                        if (values.neuteredSpayed === "true") {
                            neuteredSpayed = true;
                        } else {
                            neuteredSpayed = false;
                        }
                        try {
                            setIsLoading(true);
                            const userInsurance = values.insuranceProvider !== "" ? insurances.filter(item => item.label === values.insuranceProvider)[0].value : null;
                            const otherInsurance = values.otherInsuranceProvider !== "" ? values.otherInsuranceProvider : "";
                            const body = {
                                ownerId: user.userId,
                                sex: values.sex,
                                neuteredSpayed: neuteredSpayed,
                                insuranceProvider: userInsurance,
                                otherInsuranceProvider: otherInsurance,
                                clinicEmail: values.clinicEmail,
                                ...pet
                            };
                            delete body.id;
                            delete body.created;
                            delete body.imageUrl;
                            await dispatch(editAnimal(petId, body));
                            changeCount(1);
                        } catch (error) {
                            setIsLoading(false);
                            enqueueSnackbar(error.message || error.errorMessage, {
                                variant: "error",
                                autoHideDuration: 3000,
                            });
                        }
                    }}
                    validationSchema={validationSchema}
                >
                    {({isSubmitting, errors, values, handleChange, setFieldValue}) => (
                        <Form>
                            <Grid container spacing={2} className={classes.spacing} alignItems="center">
                                <Grid item container alignItems="center" xs={12}>
                                    <Grid item xs={3} sm={3}>
                                        <Typography variant="body2">
                                            Sex
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={9} sm={9}>
                                        <Field component={RadioGroup} name="sex" value={values.sex} row>
                                            <FormControlLabel value="MALE" control={<Radio/>} label="Male"/>
                                            <FormControlLabel value="FEMALE" control={<Radio/>} label="Female"/>
                                        </Field>
                                        <ErrorMessage name="sex">
                                            {msg => <Typography className={classes.formError}>{msg}</Typography>}
                                        </ErrorMessage>
                                    </Grid>
                                </Grid>
                                <Grid item container alignItems="center" xs={12}>
                                    <Grid item xs={4} sm={3}>
                                        <Typography variant="body2">
                                            {values.sex === "FEMALE" ? "Spayed" : "Neutered"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8} sm={9}>
                                        <Field component={RadioGroup} name="neuteredSpayed" value={values.neuteredSpayed}
                                            onChange={handleChange} row>
                                            <FormControlLabel value="true" control={<Radio/>} label="Yes"/>
                                            <FormControlLabel value="false" control={<Radio/>} label="No"/>
                                        </Field>
                                        <ErrorMessage name="neuteredSpayed">
                                            {msg => <Typography className={classes.formError}>{msg}</Typography>}
                                        </ErrorMessage>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid container justify="flex-start" spacing={2} className={classes.spacing}>
                                <Grid item xs={12} sm={3}>
                                    <Button
                                        fullWidth
                                        color="secondary"
                                        variant="outlined"
                                        type="submit"
                                        data-testid="submitButton"
                                    >
                                    Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>}
        </Container>
    );
};

export default PetDetails;
